// Nuevo ticket (artboard b adaptado): pegar un mensaje y dejar que la IA (o la
// heurística, sin llave) proponga categoría, prioridad, área y fecha; una
// persona revisa y crea. También alta manual con el mismo formulario.
import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import {
  Boton,
  Campo,
  Input,
  Pill,
  PuntoNaranja,
  Select,
  SelectContenido,
  SelectDisparador,
  SelectOpcion,
  SelectValor,
  Semaforo,
  Textarea,
  TituloPagina,
  useToast,
} from "@iavmx/ui";
import { leer } from "../api";
import { useSesion } from "../sesion";
import { useCatalogo, useRefrescar, type Interpretacion } from "./datos";
import { Migas, PorConfirmar, Segmento } from "./comunes";
import { ETIQUETA_CANAL, ETIQUETA_PRIORIDAD, fechaHora, fechaLarga, slaTexto } from "./formato";

type Modo = "mensaje" | "formulario";
type Prioridad = "baja" | "normal" | "alta" | "urgente";
const CANALES = ["whatsapp", "correo", "slack", "junta", "verbal"] as const;

interface Formulario {
  titulo: string;
  descripcion: string;
  categoriaId: string;
  prioridad: Prioridad;
  areaDestinoId: string;
  fechaCompromiso: string;
}

const vacio: Formulario = { titulo: "", descripcion: "", categoriaId: "", prioridad: "normal", areaDestinoId: "", fechaCompromiso: "" };

export function NuevoTicket() {
  const { api } = useSesion();
  const navegar = useNavigate();
  const { avisar } = useToast();
  const refrescar = useRefrescar();
  const cat = useCatalogo();
  const [modo, setModo] = React.useState<Modo>("mensaje");
  const [texto, setTexto] = React.useState("");
  const [canal, setCanal] = React.useState<(typeof CANALES)[number]>("whatsapp");
  const [propuesta, setPropuesta] = React.useState<Interpretacion | null>(null);
  const [f, setF] = React.useState<Formulario>(vacio);
  const [errores, setErrores] = React.useState<Partial<Record<keyof Formulario, string>>>({});

  const interpretar = useMutation({
    mutationFn: () => api.api.tickets.interpretar.$post({ json: { texto, canal } }).then((r) => leer<Interpretacion>(r)),
    onSuccess: (r) => {
      setPropuesta(r);
      setF({
        titulo: r.propuesta.titulo,
        descripcion: r.propuesta.resumen === texto.trim() ? "" : r.propuesta.resumen,
        categoriaId: r.propuesta.categoriaId ?? "",
        prioridad: r.propuesta.prioridad,
        // Sin área propuesta, el área destino sigue a la categoría.
        areaDestinoId:
          r.propuesta.areaDestinoId ??
          cat.data?.categorias.find((c) => c.id === r.propuesta.categoriaId)?.areaPredeterminadaId ??
          "",
        fechaCompromiso: r.propuesta.fechaCompromiso ?? "",
      });
      setErrores({});
    },
    onError: (e) => avisar({ titulo: "No se pudo proponer el ticket", descripcion: e.message, tono: "error" }),
  });

  const crear = useMutation({
    mutationFn: () =>
      api.api.tickets
        .$post({
          json: {
            titulo: f.titulo.trim(),
            descripcion: f.descripcion.trim() || undefined,
            textoOriginal: modo === "mensaje" ? texto : undefined,
            canal: modo === "mensaje" ? canal : "formulario",
            categoriaId: f.categoriaId,
            prioridad: f.prioridad,
            areaDestinoId: f.areaDestinoId || null,
            fechaCompromiso: f.fechaCompromiso || null,
            propuestaId: propuesta?.propuestaId ?? null,
          },
        })
        .then((r) => leer<{ ticket: { id: string; folio: number } }>(r)),
    onSuccess: async (r) => {
      await refrescar();
      avisar({ titulo: "Ticket creado", descripcion: "Entró como Nuevo; la jefatura del área recibió aviso para asignarlo.", tono: "exito" });
      await navegar({ to: "/tickets/$id", params: { id: r.ticket.id } });
    },
    onError: (e) => avisar({ titulo: "No se pudo crear el ticket", descripcion: e.message, tono: "error" }),
  });

  const categoria = cat.data?.categorias.find((c) => c.id === f.categoriaId) ?? null;
  const areaDestino = cat.data?.areas.find((a) => a.id === f.areaDestinoId) ?? null;
  const sla = categoria
    ? (categoria.sla.find((s) => s.prioridad === f.prioridad)?.horas ??
      (() => {
        const n = categoria.sla.find((s) => s.prioridad === "normal")?.horas;
        if (!n) return null;
        return f.prioridad === "urgente" ? Math.max(1, Math.round(n / 3)) : f.prioridad === "alta" ? Math.max(1, Math.round(n / 2)) : n;
      })())
    : null;
  const jefeDestino = areaDestino ? cat.data?.personas.find((p) => p.id === areaDestino.jefeId) : null;

  function validar(): boolean {
    const e: typeof errores = {};
    if (f.titulo.trim().length < 3) e.titulo = "Ponle un título de al menos tres letras.";
    if (!f.categoriaId) e.categoriaId = "Elige una categoría: de ella sale el SLA.";
    setErrores(e);
    return Object.keys(e).length === 0;
  }

  const formulario = (
    <div className="grid gap-4 md:grid-cols-2">
      <Campo etiqueta="Título" htmlFor="titulo" error={errores.titulo} className="md:col-span-2">
        <Input id="titulo" value={f.titulo} onChange={(e) => setF({ ...f, titulo: e.target.value })} placeholder="Qué se pide, en una línea" className="text-base md:text-sm" maxLength={140} />
      </Campo>
      <Campo etiqueta="Categoría" htmlFor="categoria" error={errores.categoriaId} ayuda={categoria ? `SLA ${slaTexto(categoria.sla.find((s) => s.prioridad === "normal")?.horas)} en prioridad normal` : "De la categoría sale el SLA y el área destino."}>
        <Select value={f.categoriaId} onValueChange={(v) => setF({ ...f, categoriaId: v, areaDestinoId: cat.data?.categorias.find((c) => c.id === v)?.areaPredeterminadaId ?? "" })}>
          <SelectDisparador id="categoria" className="text-base md:text-sm">
            <SelectValor placeholder="Elige una categoría" />
          </SelectDisparador>
          <SelectContenido>
            {cat.data?.categorias.map((c) => (
              <SelectOpcion key={c.id} value={c.id}>
                {c.nombre}
              </SelectOpcion>
            ))}
          </SelectContenido>
        </Select>
      </Campo>
      <Campo etiqueta="Prioridad" htmlFor="prioridad" ayuda="Urgente si lo dice el mensaje o es en menos de 48 h; la puede cambiar Estrategia Digital.">
        <Select value={f.prioridad} onValueChange={(v) => setF({ ...f, prioridad: v as Prioridad })}>
          <SelectDisparador id="prioridad" className="text-base md:text-sm">
            <SelectValor />
          </SelectDisparador>
          <SelectContenido>
            {(["baja", "normal", "alta", "urgente"] as const).map((p) => (
              <SelectOpcion key={p} value={p}>
                {ETIQUETA_PRIORIDAD[p]}
              </SelectOpcion>
            ))}
          </SelectContenido>
        </Select>
      </Campo>
      <Campo etiqueta="Área que lo resuelve" htmlFor="area" ayuda="Su jefatura recibe el aviso y asigna a alguien.">
        <Select value={f.areaDestinoId || "_"} onValueChange={(v) => setF({ ...f, areaDestinoId: v === "_" ? "" : v })}>
          <SelectDisparador id="area" className="text-base md:text-sm">
            <SelectValor placeholder="Elige el área" />
          </SelectDisparador>
          <SelectContenido>
            <SelectOpcion value="_">Sin área todavía · lo decide Estrategia Digital</SelectOpcion>
            {cat.data?.areas.map((a) => (
              <SelectOpcion key={a.id} value={a.id}>
                {a.nombre}
              </SelectOpcion>
            ))}
          </SelectContenido>
        </Select>
      </Campo>
      <Campo etiqueta="Fecha en que lo necesitan" htmlFor="fecha" ayuda={f.fechaCompromiso ? fechaLarga(f.fechaCompromiso) : "Opcional. La fecha límite formal la pone el SLA."}>
        <Input id="fecha" type="date" value={f.fechaCompromiso} onChange={(e) => setF({ ...f, fechaCompromiso: e.target.value })} className="text-base md:text-sm" />
      </Campo>
      <Campo etiqueta="Descripción" htmlFor="descripcion" className="md:col-span-2" ayuda="Lo que el área necesita saber para empezar.">
        <Textarea id="descripcion" value={f.descripcion} onChange={(e) => setF({ ...f, descripcion: e.target.value })} className="min-h-20 text-base md:text-sm" maxLength={4000} />
      </Campo>
    </div>
  );

  const pie = (
    <div className="flex flex-col gap-4 border-t border-linea pt-4 md:flex-row md:items-center md:justify-between">
      <p className="max-w-lg font-ui text-sm text-tinta-2">
        Al crear, el ticket entra como <strong>Nuevo</strong>
        {areaDestino ? (
          <>
            {" "}
            y {jefeDestino ? `${jefeDestino.nombre} (jefatura de ${areaDestino.nombre})` : `Estrategia Digital (${areaDestino.nombre} no tiene jefatura en el directorio)`} recibe aviso para asignarlo.
          </>
        ) : (
          <> y Estrategia Digital recibe aviso para decidir el área.</>
        )}
        {sla !== null && (
          <>
            {" "}
            Fecha límite por SLA: <strong>{slaTexto(sla)}</strong> desde ahora. <PorConfirmar que="SLA por categoría" />
          </>
        )}
      </p>
      <div className="flex gap-2">
        <Boton variante="contorno" type="button" onClick={() => void navegar({ to: "/tickets" })}>
          Cancelar
        </Boton>
        <Boton variante="acento" type="button" onClick={() => validar() && crear.mutate()} disabled={crear.isPending}>
          {crear.isPending ? "Creando…" : "Crear ticket"}
        </Boton>
      </div>
    </div>
  );

  return (
    <>
      <Migas partes={[{ texto: "Tickets", to: "/tickets" }, { texto: "Nuevo ticket" }]} />
      <TituloPagina
        descripcion="Pega el mensaje tal como llegó o llena el formulario. La interfaz propone; tú confirmas."
        accion={
          <Segmento
            etiqueta="Forma de captura"
            valor={modo}
            onChange={setModo}
            opciones={[
              { valor: "mensaje", texto: "Pegar mensaje o correo" },
              { valor: "formulario", texto: "Formulario" },
            ]}
          />
        }
      >
        Nuevo ticket
        <PuntoNaranja />
      </TituloPagina>

      {modo === "formulario" ? (
        <div className="flex max-w-3xl flex-col gap-6">
          {formulario}
          {pie}
        </div>
      ) : (
        <div className="grid gap-10 lg:grid-cols-[480px_minmax(0,1fr)]">
          <div className="flex flex-col gap-4">
            <div>
              <h2 className="font-titulo text-lg font-bold">Mensaje original</h2>
              <p className="font-ui text-sm text-tinta-tenue">WhatsApp, Slack o correo. Se guarda junto al ticket como referencia.</p>
            </div>
            <Campo etiqueta="Mensaje" htmlFor="mensaje">
              <Textarea id="mensaje" value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Karla, necesito el arte del suplemento NFL para el jueves" className="min-h-48 text-base md:text-sm" maxLength={4000} />
            </Campo>
            <div className="flex flex-wrap items-end gap-3">
              <Campo etiqueta="Llegó por" htmlFor="canal" className="w-44">
                <Select value={canal} onValueChange={(v) => setCanal(v as typeof canal)}>
                  <SelectDisparador id="canal" className="text-base md:text-sm">
                    <SelectValor />
                  </SelectDisparador>
                  <SelectContenido>
                    {CANALES.map((c) => (
                      <SelectOpcion key={c} value={c}>
                        {ETIQUETA_CANAL[c]}
                      </SelectOpcion>
                    ))}
                  </SelectContenido>
                </Select>
              </Campo>
              <Boton variante="primario" type="button" onClick={() => interpretar.mutate()} disabled={texto.trim().length < 5 || interpretar.isPending}>
                {interpretar.isPending ? "Proponiendo…" : propuesta ? "Volver a proponer" : "Proponer ticket"}
              </Boton>
            </div>
            <div className="flex flex-col gap-2 bg-fondo-suave p-5 font-ui text-sm text-tinta-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-tinta-tenue">Cómo funciona</span>
              <p>La IA clasifica, detecta la fecha y propone categoría, prioridad, área y fecha límite según el SLA. No asigna ni aprueba: eso lo hace una persona.</p>
              <p>Sin llave de IA, la propuesta sale de una heurística por palabras clave y se marca «sin IA».</p>
            </div>
          </div>

          <div className="flex flex-col gap-5">
            {!propuesta ? (
              <div className="flex flex-col items-start gap-3 border border-dashed border-linea-fuerte p-8">
                <h2 className="font-titulo text-xl font-bold">
                  Ticket propuesto
                  <PuntoNaranja />
                </h2>
                <p className="max-w-prose font-ui text-sm text-tinta-tenue">Pega el mensaje y pulsa «Proponer ticket». Aquí aparece la propuesta para que la revises antes de crearlo.</p>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-titulo text-lg font-bold">Ticket propuesto</h2>
                    {propuesta.origen === "ia" ? <Pill tono="azul">Propuesta de la IA</Pill> : <Pill tono="neutro">Propuesta heurística · sin IA</Pill>}
                  </div>
                  {propuesta.propuesta.camposFaltantes.length > 0 ? (
                    <Semaforo color="ambar">Falta: {propuesta.propuesta.camposFaltantes.join(", ")}</Semaforo>
                  ) : (
                    <Semaforo color="verde">Nada falta en el mensaje</Semaforo>
                  )}
                </div>
                <p className="font-ui text-sm text-tinta-2">
                  {propuesta.propuesta.motivo}
                  {propuesta.propuesta.venceEn && <> Vencería el {fechaHora(propuesta.propuesta.venceEn)} por SLA.</>}
                  {propuesta.aviso && <span className="text-tinta-tenue"> {propuesta.aviso}</span>}
                </p>
                {formulario}
                {pie}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
